import { createRoute, Link, redirect } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { useState, type FormEvent } from 'react';

import { useRequestPasswordReset } from 'src/api/auth/useRequestPasswordReset';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/ui/spinner';
import { Route as rootRoute } from 'src/pages/__root';

import { AuthErrorMessage } from './AuthErrorMessage';
import { AuthShell } from './AuthShell';

export const Route = createRoute({
  path: '/forgot-password',
  getParentRoute: () => rootRoute,
  beforeLoad: ({ context }) => {
    if (context.auth.currentUser) {
      throw redirect({ to: '/' });
    }
  },
  component: ForgotPasswordPage,
});

export function ForgotPasswordPage() {
  const gt = useGT();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { mutate, isPending, error } = useRequestPasswordReset();
  const isLoading = isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!email.trim()) {
      setEmailError(
        gt('Email is required.', {
          context:
            'validation error shown when the email field is empty on the forgot-password form',
        }),
      );
      return;
    }
    setEmailError('');

    mutate({ email: email.trim() }, { onSuccess: () => setSubmitted(true) });
  }

  if (submitted) {
    return (
      <AuthShell
        washOut={false}
        title={
          <T context="heading on the forgot-password confirmation screen">
            Check your email
          </T>
        }
        description={
          <T context="confirmation copy shown after a forgot-password request succeeds">
            We&rsquo;ve sent a link to reset your password. The link expires in
            1 hour.
          </T>
        }
      >
        <Link
          to="/login"
          className="block text-center text-sm font-medium text-[#1a1a1a] hover:underline mt-2"
        >
          <T context="link back to the login page from the forgot-password confirmation">
            Back to log in
          </T>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      washOut={false}
      title={
        <T context="heading on the forgot-password form">
          Forgot your password?
        </T>
      }
      description={
        <T context="description on the forgot-password form">
          Enter your email and we&rsquo;ll send you a link to reset it.
        </T>
      }
    >
      <form noValidate onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="forgot-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {gt('Email', {
                context: 'label for email input on the forgot-password page',
              })}
            </label>
            <input
              id="forgot-email"
              type="email"
              value={email}
              disabled={isLoading}
              autoFocus
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-600">{emailError}</p>
            )}
          </div>
        </div>

        <Button type="submit" disabled={isLoading} className="w-full mt-6">
          {isLoading ? (
            <Spinner className="mx-auto text-white" />
          ) : (
            <T context="button label to submit the forgot-password form">
              Send reset link
            </T>
          )}
        </Button>

        <AuthErrorMessage error={error} className="mt-4" />

        <Link
          to="/login"
          className="block text-center text-sm text-gray-500 hover:text-[#1a1a1a] mt-4"
        >
          <T context="link back to the login page from the forgot-password form">
            Back to log in
          </T>
        </Link>
      </form>
    </AuthShell>
  );
}
